// vendors
import React from "react"
import { graphql } from "gatsby"
import { css } from "@emotion/core"
import VisuallyHidden from "@reach/visually-hidden"

// utils
import media from "./../../utils/media-query"
import mediaQuery, { greaterThen } from "../../utils/media-query"

// images
import iconLanguage from "../../images/icon-language.svg"
import iconPopulation from "../../images/icon-population.svg"
import iconSuperficies from "../../images/icon-superficies.svg"
import iconTemperature from "../../images/icon-temperature.svg"
import iconMap from "../../images/icon-map.svg"
import iconFlag from "../../images/icon-flag.svg"
import iconSuitcase from "../../images/icon-suitcase.svg"

// components
import Layout from "../../components/Layout"
import Hero from "../../components/Hero"
import Link from "../../components/Link"
import Seo from "../../components/Seo"

// self components
import Values, { Value } from "./_components/values"
import CaracteristicsGrid from "./_components/CaracteristicsGrid"
import SectionHeader from "../../components/SectionHeader/SectionHeader"
import BlockContent from "../../components/BlockContent/BlockContent"
import SidebarLayout from "../../components/SidebarLayout/SidebarLayout"
import RegionGrid from "./_components/regionGrid"
import wrapper from "../../utils/wrapper"
import LinkGroup from "../../components/LinkGroup"
import ContactCard from "../../components/ContactCard"
import { colors, fontSizes } from "../../styles/variables"
import RegionMap from "../../components/RegionMap/RegionMap"
import Button from "../../components/Button"
import { between } from "polished"

const breakpoint = 1200

const TemplateRegionPost = ({ data, errors }) => {
  const region = data && data.region
  const { title, localActivity } = region
  let { slides } = region

  slides = slides.map((slide) => slide.asset.fluid)

  const sidebar = (
    <div
      css={css`
        height: 100%;

        ${mediaQuery.lessThen(breakpoint)} {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
        }
      `}
    >
      <div
        css={css`
          ${mediaQuery.lessThen(breakpoint)} {
            order: 2;
            flex-grow: 1;
          }
        `}
      >
        <RegionMap
          selectedRegion={region.meta.slug.current}
          css={css`
            display: block;
            max-width: 300px;
            margin: auto;
          `}
        />
      </div>

      <nav
        css={css`
          ${greaterThen(breakpoint)} {
            position: sticky;
            top: 250px;
            margin-bottom: 7.25em;
          }
        `}
      >
        <ul
          css={css`
            list-style: none;
            margin: 27px 0;
            padding: 0;

            ${greaterThen(breakpoint)} {
              ${wrapper.paddingLeft()}
              ${wrapper.paddingRight()}
            }

            li {
              margin: 0;
            }
          `}
        >
          <li>
            <Link tag="a" href="#caracteristique">
              Caractéristiques
              <br />
              de&nbsp;la&nbsp;région
            </Link>
          </li>
          <li>
            <Link tag="a" href="#la-pratique">
              La pratique
            </Link>
          </li>
          <li>
            <Link tag="a" href="#vie-quotidienne">
              Vie quotidienne
            </Link>
          </li>
          <li>
            <Link tag="a" href="#coordonnees">
              Coordonnées
            </Link>
          </li>
        </ul>
      </nav>
    </div>
  )

  return (
    <Layout>
      <Seo
        description={region.meta.description}
        lang="fr"
        keywords={region.meta.keyword}
        title={region.meta.title}
      />

      <article>
        <Hero title={title} slides={slides} />

        <SidebarLayout
          sidebar={sidebar}
          breakpoint={breakpoint}
          css={css`
            margin-top: ${fontSizes[5] / fontSizes[1]}em;
          `}
        >
          <section id="caracteristique">
            <VisuallyHidden>
              <header>
                <h2>Caractéristiques de la région</h2>
              </header>
            </VisuallyHidden>

            <Values>
              <Value
                icon={iconPopulation}
                title="Population"
                value={`~${Intl.NumberFormat().format(region.population)}`}
              />

              <Value
                icon={iconSuperficies}
                title="Superficie de la région"
                value={`${Intl.NumberFormat().format(region.size)} km²`}
              />

              <Value
                icon={iconTemperature}
                title="Température"
                value={`${region.temperatureMin}/${region.temperatureMax} ˚C`}
              />

              <Value
                icon={iconLanguage}
                title="Langues parlées"
                value={region.langage
                  .reduce((acc, cur, index) => {
                    if (!Math.floor(index / 2)) acc.push([])

                    acc[Math.floor(index / 2)].push(cur)

                    return acc
                  }, [])
                  .map((cur) => cur.join("/"))
                  .join("<br/>")}
              />
            </Values>

            <CaracteristicsGrid region={region} />

            <h3>Activités locales</h3>

            <p
              css={css`
                max-width: 79ch;
              `}
              dangerouslySetInnerHTML={{ __html: localActivity }}
            />
          </section>

          <section id="la-pratique">
            <SectionHeader
              title="La pratique"
              picture={region.practiceImage && region.practiceImage.asset.fluid}
              pattern="1"
              breakpoint={breakpoint}
            />

            <BlockContent blocks={region._rawPracticeText} />
          </section>

          <section id="vie-quotidienne">
            <SectionHeader
              title="Vie quotidienne"
              picture={
                region.everydayLifePicture &&
                region.everydayLifePicture.asset.fluid
              }
              pattern="2"
              breakpoint={breakpoint}
            />

            <div
              css={css`
                padding-top: ${fontSizes[7] / fontSizes[1]}em;
              `}
            >
              <BlockContent blocks={region._rawEverydayLifeText} />
            </div>

            {region.meta.slug.current &&
              region.meta.slug.current !== "terres-cries-de-la-baie-james" && (
                <div
                  css={css`
                    margin-top: ${72 / fontSizes[1]}rem;
                    padding: ${40 / fontSizes[1]}rem;

                    ${media.greaterThen(1024)} {
                      padding: ${60 / fontSizes[1]}rem ${76 / fontSizes[1]}rem;
                    }
                    background-color: ${colors.veryLightGreen};
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      flex-wrap: wrap;
                      gap: ${54 / fontSizes[1]}rem;
                      align-items: center;
                    `}
                  >
                    <div
                      css={css`
                        flex-grow: 1;
                        flex-basis: calc((42rem - 100%) * 999);
                      `}
                    >
                      <p
                        css={css`
                          font-size: ${40 / fontSizes[1]}em;
                          font-weight: 800;
                          line-height: ${44 / 40};
                        `}
                      >
                        Un avant-goût
                        <br /> de la région?
                      </p>

                      <div
                        css={css`
                          display: inline-flex;
                          gap: ${22 / fontSizes[1]}rem;
                          /* max-height: 38px; */

                          margin-left: 6px;

                          > img {
                            max-height: 38px;
                          }
                        `}
                      >
                        <img src={iconMap} alt="" role="presentation" />
                        <img src={iconSuitcase} alt="" role="presentation" />
                        <img src={iconFlag} alt="" role="presentation" />
                      </div>
                    </div>

                    <div
                      css={css`
                        flex-grow: 1;
                        flex-basis: calc((42rem - 100%) * 999);
                      `}
                    >
                      <div>
                        <p
                          css={css`
                            margin-bottom: 2.2em;
                          `}
                        >
                          Avant de faire le saut chez nous, explorez les lieux à
                          travers nos images 360° degrés qui vous donneront un
                          avant-goût de ce qui vous attend!
                        </p>

                        <Button
                          tag="a"
                          href={`https://visite-virtuelle.equipesarros.ca/${region.meta.slug.current}-360/`}
                          secondary
                          target="_blank"
                          rel="nnoopener noreferrer"
                          css={{ margin: "0 !important" }}
                        >
                          Voir les images 360&deg;
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
          </section>

          <section id="coordonnees">
            <header>
              <h2
                css={css`
                  word-break: break-all;
                  word-break: break-word; /* safari fix */
                  hyphens: auto;
                  margin-top: 2.3em;
                  margin-bottom: 1em;
                `}
              >
                Coor&shy;données
              </h2>
            </header>

            <ContactCard contact={region.contact} noWhiteSpace />
          </section>

          <LinkGroup>
            <Link to="/emploi">
              En savoir plus
              <br />
              sur&nbsp;l'emploi
            </Link>

            <Link to="/stage">
              En savoir plus
              <br />
              sur&nbsp;les&nbsp;stages
            </Link>
          </LinkGroup>
        </SidebarLayout>
      </article>

      <RegionGrid actual={region.title} />
    </Layout>
  )
}

export default TemplateRegionPost

export const query = graphql`
  query RegionTemplateQuery($id: String!) {
    region: sanityRegion(id: { eq: $id }) {
      langage
      title
      contact {
        email
        extension
        name
        organisation
        phone
      }
      localActivity
      localSpeciality
      meta {
        description
        keyword
        slug {
          current
        }
        title
      }
      population
      quote {
        cite
        quote
      }
      regionNumber
      size
      temperatureMax
      temperatureMin
      regionMRC {
        name
        url
      }
      _rawEverydayLifeText
      _rawPracticeText
      everydayLifePicture {
        asset {
          fluid(maxWidth: 508) {
            srcSet
            srcSetWebp
            srcWebp
          }
        }
      }
      practiceImage {
        asset {
          fluid(maxWidth: 564) {
            src
            srcSet
            srcSetWebp
          }
        }
      }
      slides: slider {
        asset {
          fluid(maxWidth: 960) {
            src
            srcSet
            srcSetWebp
          }
        }
      }
    }
  }
`
